import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionDecorations, ActionFeedback, ActionInput, ActionMedia, ActionPrivacy, ActionSubmit, ActionTags, ActionText } from '@bonusly/common-action-input';
import { Box, Checkbox, Circle, Flex, Hide, LockClosedOutline, ModalBody, ModalFooter, rem, Text, Tooltip } from '@bonusly/design-system';

import { AwardHeader } from './AwardHeader';
import { useAwardContent } from './hooks/useAwardContent';
import { useAwardModal } from './hooks/useAwardModal';
import { useTemplatedAward } from './hooks/useTemplatedAward';
import { useTemplatePreview } from './hooks/useTemplatePreview';
import { RequiresApprovalBanner } from './RequiresApprovalBanner';
import { useAwardModalState } from './state';

export const TemplatedAwardInput = () => {
	const { onClose } = useAwardModal();
	const { hideGiver, selectedAward, setHideGiver, setIsSubmitting } = useAwardModalState();
	const { t } = useTranslation('recognition');
	const config = useTemplatedAward(selectedAward, { onSuccess: onClose });
	const content = useAwardContent();

	useEffect(() => {
		setIsSubmitting(config.isSubmitting);
	}, [config.isSubmitting, setIsSubmitting]);

	return (
		<ActionInput autofocus="end" content={content} {...config}>
			<ModalBody py={4}>
				<AwardHeader mb={4} />

				<Flex alignItems="flex-start" justifyContent="space-between">
					<ActionTags />
					<Hide below="xs">
						<ActionPrivacy />
					</Hide>
				</Flex>

				<Box mb={2}>
					<ActionText inputStyles={{ minHeight: rem(75) }} />
					<Text as="span">
						<Box as="span" fontWeight="medium">
							{t('awards.give_modal.template_label')}:
						</Box>

						{useTemplatePreview(selectedAward)}

						<Box as="span" display="inline-block" ml={1}>
							<Tooltip label={t('awards.give_modal.template_tooltip_custom_message')}>
								<Circle bg="blackAlpha.50" size={rem(32)}>
									<LockClosedOutline size="sm" />
								</Circle>
							</Tooltip>
						</Box>
						<Box mt={2}>
							<Checkbox isChecked={!hideGiver} onChange={(event) => setHideGiver(!event.target.checked)}>
								<Text mt={1}>{t('awards.give_modal.show_giver_label')}</Text>
							</Checkbox>
						</Box>
					</Text>
					<ActionFeedback />
				</Box>

				<ActionMedia w="full" />
			</ModalBody>

			<ModalFooter borderTop="1px solid" borderColor="gray.100" flexDirection="column" alignItems="initial">
				{!!selectedAward?.approval_process && (
					<RequiresApprovalBanner
						title={t('awards.give_modal.requires_approval_title')}
						description={t('awards.give_modal.requires_approval_description')}
						mb={4}
					/>
				)}

				<Flex
					alignItems={{ base: 'stretch', xs: 'flex-start' }}
					direction={{ base: 'column', xs: 'row' }}
					gap={2}
					justifyContent="space-between"
					mb={2}
					w="full"
				>
					<Flex justifyContent="space-between" flexGrow={1}>
						<ActionDecorations />
						<Hide above="xs">
							<ActionPrivacy />
						</Hide>
					</Flex>

					<ActionSubmit size="lg" />
				</Flex>
			</ModalFooter>
		</ActionInput>
	);
};
